import { useEffect, useSyncExternalStore } from "react";

type Status = "idle" | "loading" | "success" | "error";

interface ResourceState<T> {
  data: T | null;
  status: Status;
  error: string | null;
}

// Cache global (fora do React) de um recurso da API, compartilhado por todos
// os componentes montados: deduplica requisições concorrentes e notifica os
// assinantes a cada mudança. Use `refresh()` após mutations.
// `fetcher` recebe `force` para ignorar caches do próprio service.
export function createResource<T>(fetcher: (force: boolean) => Promise<T>) {
  let state: ResourceState<T> = { data: null, status: "idle", error: null };
  let pending: Promise<T> | null = null;
  const listeners = new Set<() => void>();

  const setState = (next: Partial<ResourceState<T>>) => {
    state = { ...state, ...next };
    listeners.forEach((listener) => listener());
  };

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const load = async (force = false): Promise<T> => {
    if (pending) {
      if (!force) return pending;
      // `refresh()` depois de uma mutation não pode reaproveitar uma busca
      // iniciada antes dela (traria o dado antigo, ex.: a imagem anterior de um
      // bloco). Espera essa busca terminar e busca de novo.
      await pending.catch(() => undefined);
      if (pending) return pending; // outro refresh já começou depois da mutation
    }
    if (!force && state.status === "success" && state.data !== null) return state.data;

    // Com dado em cache, atualiza em segundo plano (sem voltar a "loading").
    setState({ status: state.data !== null ? state.status : "loading", error: null });
    pending = fetcher(force);
    try {
      const data = await pending;
      setState({ data, status: "success", error: null });
      return data;
    } catch (err) {
      setState({
        status: "error",
        error: err instanceof Error ? err.message : "Erro ao carregar dados.",
      });
      throw err;
    } finally {
      pending = null;
    }
  };

  // `enabled = false` só lê o cache, sem disparar a requisição.
  const useResource = (enabled = true) => {
    const snapshot = useSyncExternalStore(subscribe, () => state);

    useEffect(() => {
      if (enabled && state.status === "idle") void load().catch(() => undefined);
    }, [enabled]);

    return {
      data: snapshot.data,
      loading: enabled && (snapshot.status === "idle" || snapshot.status === "loading"),
      error: snapshot.error,
      refresh: () => load(true),
    };
  };

  return {
    load,
    refresh: () => load(true),
    get: () => state.data,
    reset: () => setState({ data: null, status: "idle", error: null }),
    useResource,
  };
}
